import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calculator, 
  TrendingUp, 
  Clock, 
  Euro,
  FileText,
  ArrowUpRight,
  Activity,
  Target
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@plvplus/core';

interface PriPlusMetrics {
  totalCalculations: number;
  calculationsThisMonth: number;
  averageMargin: number;
  totalRevenue: number;
  pendingQuotes: number;
  completionRate: number;
}

interface RecentCalculation {
  id: string;
  projectName: string;
  client: string;
  margin: number;
  revenue: number;
  status: 'completed' | 'pending' | 'approved';
  createdAt: string;
}

interface PriPlusOverviewProps {
  showQuickActions?: boolean;
  compact?: boolean;
}

export const PriPlusOverview: React.FC<PriPlusOverviewProps> = ({ 
  showQuickActions = true, 
  compact = false 
}) => {
  // Mock data - replace with real API data
  const metrics: PriPlusMetrics = {
    totalCalculations: 156,
    calculationsThisMonth: 23,
    averageMargin: 28.5,
    totalRevenue: 45680,
    pendingQuotes: 8,
    completionRate: 92
  };

  const recentCalculations: RecentCalculation[] = [
    {
      id: '1',
      projectName: 'Présentoir Pharmacie',
      client: 'Pharmacie Martin',
      margin: 32.5,
      revenue: 1250,
      status: 'completed',
      createdAt: '2024-01-15T10:30:00Z'
    },
    {
      id: '2',
      projectName: 'PLV Carrefour',
      client: 'Carrefour City',
      margin: 25.0,
      revenue: 2100,
      status: 'approved',
      createdAt: '2024-01-14T16:45:00Z'
    },
    {
      id: '3',
      projectName: 'Stand Bio&Co',
      client: 'Bio&Co',
      margin: 30.0,
      revenue: 890,
      status: 'pending',
      createdAt: '2024-01-14T09:15:00Z'
    }
  ];

  const quickActions = [
    {
      title: 'Nouveau calcul',
      description: 'Créer un calcul PRI+',
      icon: Calculator,
      href: '/pri-plus/calculations/new',
      color: 'bg-blue-500'
    },
    {
      title: 'Voir calculs',
      description: 'Liste des calculs',
      icon: FileText,
      href: '/pri-plus/calculations',
      color: 'bg-green-500'
    },
    {
      title: 'Devis en attente',
      description: `${metrics.pendingQuotes} devis`,
      icon: Clock,
      href: '/pri-plus/quotes?status=pending',
      color: 'bg-orange-500'
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 dark:text-green-400';
      case 'approved': return 'text-blue-600 dark:text-blue-400';
      case 'pending': return 'text-orange-600 dark:text-orange-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed': return <Badge variant="success">Terminé</Badge>;
      case 'approved': return <Badge variant="default">Approuvé</Badge>;
      case 'pending': return <Badge variant="warning">En attente</Badge>;
      default: return <Badge variant="outline">Inconnu</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(new Date(dateString));
  };

  if (compact) {
    return (
      <Card variant="elevated">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              PRI+ Aperçu
            </CardTitle>
            <Link to="/pri-plus">
              <Button variant="outline" size="sm">
                Ouvrir PRI+
                <ArrowUpRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {metrics.calculationsThisMonth}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Calculs ce mois
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                {metrics.averageMargin}%
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Marge moyenne
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* PRI+ Header */}
      <Card variant="elevated">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Calculator className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-xl">PRI+ Calculs de Prix</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Module de calcul des prix de revient
                </p>
              </div>
            </div>
            <Link to="/pri-plus">
              <Button variant="outline">
                Ouvrir PRI+
                <ArrowUpRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Calculs totaux
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {metrics.totalCalculations}
                </p>
              </div>
              <Activity className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Ce mois
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {metrics.calculationsThisMonth}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Marge moyenne
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {metrics.averageMargin}%
                </p>
              </div>
              <Target className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card variant="elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Chiffre d'affaires
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(metrics.totalRevenue)}
                </p>
              </div>
              <Euro className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Calculations */}
        <Card variant="elevated">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Calculs récents</CardTitle>
              <Link to="/pri-plus/calculations">
                <Button variant="outline" size="sm">
                  Voir tout
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentCalculations.map((calc) => (
                <div key={calc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {calc.projectName}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {calc.client} • {formatDate(calc.createdAt)}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">
                        Marge: {calc.margin}%
                      </span>
                      <span className="text-xs text-gray-500">•</span>
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                        {formatCurrency(calc.revenue)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end space-y-1">
                    {getStatusBadge(calc.status)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        {showQuickActions && (
          <Card variant="elevated">
            <CardHeader>
              <CardTitle>Actions rapides</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {quickActions.map((action) => (
                  <Link
                    key={action.title}
                    to={action.href}
                    className="group flex items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all hover:border-primary-300 dark:hover:border-primary-600"
                  >
                    <div className={`p-2 ${action.color} rounded-lg mr-3`}>
                      <action.icon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">
                        {action.title}
                      </h3>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {action.description}
                      </p>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Performance Indicators */}
      <Card variant="elevated">
        <CardHeader>
          <CardTitle>Indicateurs de performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full mx-auto mb-2">
                <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {metrics.completionRate}%
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Taux de finalisation
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Calculs terminés avec succès
              </p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-orange-100 dark:bg-orange-900/20 rounded-full mx-auto mb-2">
                <span className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {metrics.pendingQuotes}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Devis en attente
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Nécessitent une action
              </p>
            </div>

            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/20 rounded-full mx-auto mb-2">
                <span className="text-xl font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(metrics.totalRevenue / metrics.totalCalculations)}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Valeur moyenne
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Par calcul réalisé
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
